import React, { useEffect, useMemo } from "react";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import {
  QueryPresignedURLForRadio,
  QueryRadiosForEvent,
} from "../../../utils/graphql/hooks/radioHooks";
import {
  setSelectedEvent,
  setSelectedSession,
  setSelectedType,
  setSelectedDriver,
} from "../../../state/radioReducer";

export default function RadioSelectorBar() {
  const dispatch = useDispatch();
  const queryRadiosForEvent = QueryRadiosForEvent();
  const queryPresignedURLForRadio = QueryPresignedURLForRadio();

  const availableRadioEvents = useSelector(
    (state: RootState) => state.radio?.availableRadioEvents,
    isEqual,
  );

  const activeEventSessions = useSelector(
    (state: RootState) => state.radio?.activeEventSessions,
    isEqual,
  );

  const selectedEvent = useSelector(
    (state: RootState) => state.radio?.selectedEvent,
    isEqual,
  );

  const selectedSession = useSelector(
    (state: RootState) => state.radio?.selectedSession,
    isEqual,
  );

  const selectedType = useSelector(
    (state: RootState) => state.radio?.selectedType,
    isEqual,
  );

  const selectedDriver = useSelector(
    (state: RootState) => state.radio?.selectedDriver,
    isEqual,
  );

  const isWaveformLoading = useSelector(
    (state: RootState) => state.radio?.isWaveformLoading,
    isEqual,
  );

  const queryPresignedURLSignal = useSelector(
    (state: RootState) => state.radio?.queryPresignedURLSignal,
  );

  const reversedEvents = useMemo(
    () => [...(availableRadioEvents ?? [])].reverse(),
    [availableRadioEvents],
  );

  const sessions = useMemo(() => {
    if (!activeEventSessions) return [];
    return Object.keys(activeEventSessions);
  }, [activeEventSessions]);

  const types = useMemo(() => {
    if (!selectedSession) return [];
    return ["Raw", "Summary"];
  }, [selectedSession]);

  const drivers = useMemo(() => {
    if (!activeEventSessions || !selectedSession || !selectedType) return [];
    const sessionData = activeEventSessions[selectedSession];
    if (!sessionData) return [];
    return selectedType === "Raw" ? sessionData.raw : sessionData.summary;
  }, [activeEventSessions, selectedSession, selectedType]);

  // Query presigned URL when signal changes
  useEffect(() => {
    if (selectedEvent && selectedSession && selectedType && selectedDriver) {
      queryPresignedURLForRadio(
        selectedEvent,
        selectedSession,
        selectedType,
        selectedDriver,
      );
    }
  }, [queryPresignedURLSignal]);

  return (
    <div className={styles.container}>
      <select
        className={styles.selector}
        onChange={(e) => {
          const event = e.target.value;
          dispatch(setSelectedEvent(event));
          queryRadiosForEvent(event);
        }}
        value={selectedEvent ?? ""}
        disabled={isWaveformLoading}
      >
        <option value="" disabled hidden>
          Event
        </option>
        {reversedEvents?.map((event) => (
          <option key={event} value={event}>
            {event}
          </option>
        ))}
      </select>

      <select
        className={styles.selector}
        onChange={(e) => {
          dispatch(setSelectedSession(e.target.value));
        }}
        value={selectedSession ?? ""}
        disabled={isWaveformLoading || !selectedEvent || sessions.length === 0}
      >
        <option value="" disabled hidden>
          Session
        </option>
        {sessions.map((session) => (
          <option key={session} value={session}>
            {session}
          </option>
        ))}
      </select>

      <select
        className={styles.selector}
        onChange={(e) => {
          dispatch(setSelectedType(e.target.value));
        }}
        value={selectedType ?? ""}
        disabled={isWaveformLoading || !selectedSession}
      >
        <option value="" disabled hidden>
          Type
        </option>
        {types.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>

      <select
        className={styles.selector}
        onChange={(e) => {
          dispatch(setSelectedDriver(e.target.value));
        }}
        value={selectedDriver ?? ""}
        disabled={isWaveformLoading || !selectedType || drivers.length === 0}
      >
        <option value="" disabled hidden>
          Driver
        </option>
        {drivers.map((driver) => (
          <option key={driver} value={driver}>
            {driver}
          </option>
        ))}
      </select>
    </div>
  );
}
